import { useEffect, useState } from 'react'
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom'
import { PatientHeaderStrip } from '@/components/patient/PatientHeaderStrip'
import { RecordAccessBanner } from '@/components/patient/RecordAccessBanner'
import { PatientWorkspaceTabs } from './PatientWorkspaceTabs'
import { HistoriaClinicaPanel } from '@/components/consultation/HistoriaClinicaPanel'
import { QuickNoteFab } from '@/components/patient/QuickNoteFab'
import { EditPatientDialog } from '@/components/patients/EditPatientDialog'
import { useActivePatient } from '@context/ActivePatientContext'
import { usePatient } from '@hooks/usePatients'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function PatientWorkspace() {
  const { patientId } = useParams<{ patientId: string }>()
  return <PatientWorkspaceContent key={patientId} />
}

function PatientWorkspaceContent() {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { activePatientId, activePatientData, setActivePatient, isLoading: contextLoading } = useActivePatient()

  const { data: patient, isLoading: queryLoading, isError, refetch } = usePatient(
    patientId || '',
    { enabled: !!patientId }
  )

  // Sync URL param with context
  useEffect(() => {
    if (patientId && patient?.id === patientId) {
      setActivePatient(patient)
    }
  }, [patientId, patient, setActivePatient])

  // Redirect root to the derived summary (REQ-SUM-07).
  useEffect(() => {
    if (patientId && location.pathname === `/workspace/patient/${patientId}`) {
      navigate('resumen', { replace: true })
    }
  }, [patientId, navigate, location.pathname])

  const isLoading = contextLoading || queryLoading
  const [editOpen, setEditOpen] = useState(false)

  const contextMatches = !!patientId && activePatientId === patientId && activePatientData?.id === patientId

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4" role="alert">
        <p>No se pudo cargar el paciente solicitado.</p>
        <Button onClick={() => void refetch()}>Reintentar</Button>
      </div>
    )
  }

  if (isLoading || (!contextMatches && patient?.id === patientId)) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-ml-primary" />
          <p className="text-sm text-ml-on-surface-variant">Cargando datos del paciente...</p>
        </div>
      </div>
    )
  }

  if (!contextMatches) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-lg font-headline italic text-ml-primary">Paciente no encontrado</p>
          <p className="text-sm text-ml-on-surface-variant mt-1">
            No se pudo cargar el espacio de trabajo del paciente.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ml-surface flex flex-col">
      {/* Shared header strip */}
      <PatientHeaderStrip onEditClick={() => setEditOpen(true)} />

      {/* Access banner + break-the-glass gate (REQ-AUTH-02/04). Clinical content
          is hidden until the accessor's relationship is confirmed or justified. */}
      <RecordAccessBanner patientId={activePatientData?.id ?? ''}>
        {/* Body: left section navigation + (summary/history above routed content) */}
        <div className="flex flex-1 gap-0">
          {/* Left sidebar — section navigation (vertical) */}
          {activePatientData && (
            <aside className="hidden xl:flex flex-col w-80 flex-shrink-0 border-r border-ml-outline-variant/10 px-3 py-5 sticky top-0 self-start h-screen overflow-y-auto">
              <PatientWorkspaceTabs vertical />
            </aside>
          )}

          {/* Right column */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Horizontal navigation fallback where the left sidebar is hidden */}
            <div className="xl:hidden">
              <PatientWorkspaceTabs />
            </div>

            {/* Full-width allergies / medical-history box — spans the page and
                stays pinned at the top so it remains visible while scrolling. */}
            {activePatientData && (
              <div className="sticky top-0 z-10 bg-ml-surface border-b border-ml-outline-variant/15 px-6 lg:px-12 py-4 max-h-[45vh] overflow-y-auto">
                <HistoriaClinicaPanel patientId={activePatientData.id} horizontal />
              </div>
            )}

            <main className="flex-1 px-6 lg:px-12 py-8 min-w-0">
              {/* Span the full content width (matching the header strips above)
                  so information is evenly distributed and never looks cut off
                  to one side on wide screens. */}
              <div className="w-full space-y-8">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </RecordAccessBanner>

      {/* Floating quick note FAB */}
      {activePatientData && <QuickNoteFab patientId={activePatientData.id} />}

      {/* Patient edit dialog */}
      {activePatientData && (
        <EditPatientDialog
          patient={activePatientData}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  )
}
